/**
 * CSV without a dependency: banks export the format badly enough that owning the parser is the
 * smaller problem. Austrian exports are semicolon separated, Windows-1252 encoded more often than
 * anyone would like, and write amounts as "-1.234,56".
 */

/** Guesses the separator from the first non-empty line, outside of quotes. */
export function detectDelimiter(text: string, candidates = [";", ",", "\t", "|"]): string {
  const line = text.split(/\r?\n/).find((entry) => entry.trim().length > 0) ?? "";
  let best = candidates[0]!;
  let bestCount = 0;
  for (const candidate of candidates) {
    let count = 0;
    let quoted = false;
    for (const char of line) {
      if (char === '"') quoted = !quoted;
      else if (char === candidate && !quoted) count += 1;
    }
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

/** Parses CSV with quoted fields, doubled quotes and embedded newlines. */
export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let index = 0;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    // Ignore the trailing newline of the file and other fully empty lines.
    if (row.length > 1 || row[0]!.trim().length > 0) rows.push(row);
    row = [];
  };

  const body = text.replace(/^\uFEFF/, "");
  while (index < body.length) {
    const char = body[index]!;
    if (quoted) {
      if (char === '"') {
        if (body[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        quoted = false;
        index += 1;
        continue;
      }
      field += char;
      index += 1;
      continue;
    }
    if (char === '"' && field.length === 0) {
      quoted = true;
      index += 1;
    } else if (char === delimiter) {
      endField();
      index += 1;
    } else if (char === "\n") {
      endRow();
      index += 1;
    } else if (char === "\r") {
      index += 1;
    } else {
      field += char;
      index += 1;
    }
  }
  if (field.length > 0 || row.length > 0) endRow();
  return rows;
}

/**
 * Decodes an uploaded file. George (Erste) exports UTF-16 with a byte order mark; older bank
 * exports are Windows-1252; everything else is UTF-8.
 */
export function decodeCsv(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(buffer);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(buffer);
  // UTF-16 without a BOM still gives itself away: every other byte of ASCII text is zero.
  const sample = bytes.subarray(0, 200);
  const zeros = sample.filter((byte, index) => index % 2 === 1 && byte === 0).length;
  if (sample.length > 8 && zeros > sample.length / 4) {
    return new TextDecoder("utf-16le").decode(buffer);
  }
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    try {
      return new TextDecoder("windows-1252").decode(buffer);
    } catch {
      return new TextDecoder().decode(buffer);
    }
  }
}

const needsQuotes = (value: string, delimiter: string) =>
  value.includes(delimiter) || value.includes('"') || /[\r\n]/.test(value);

/** Serialises rows back to CSV. The BOM makes Excel open UTF-8 files correctly. */
export function toCsv(
  rows: Array<Array<string | number | null | undefined>>,
  options: { delimiter?: string; bom?: boolean } = {},
): string {
  const delimiter = options.delimiter ?? ";";
  const body = rows
    .map((row) =>
      row
        .map((cell) => {
          const value = cell === null || cell === undefined ? "" : String(cell);
          return needsQuotes(value, delimiter) ? `"${value.replace(/"/g, '""')}"` : value;
        })
        .join(delimiter),
    )
    .join("\r\n");
  return (options.bom === false ? "" : "\uFEFF") + body;
}
